import { SITE_NAME, SITE_URL } from './site';

export type ArrayToolSlug =
  | 'array-length'
  | 'remove-duplicates'
  | 'sort-array'
  | 'reverse-array'
  | 'flatten-array'
  | 'chunk-array'
  | 'array-to-csv'
  | 'array-difference'
  | 'array-intersection';

export type ArrayToolCategory = 'inspect' | 'transform' | 'compare';

export interface ArrayToolRoute {
  slug: ArrayToolSlug;
  category: ArrayToolCategory;
  label: string;
  shortLabel: string;
  title: string;
  description: string;
}

export const ARRAY_TOOL_CATEGORY_LABELS: Record<ArrayToolCategory, string> = {
  inspect: 'Inspect',
  transform: 'Transform',
  compare: 'Compare',
};

export const ARRAY_TOOL_ROUTES: ArrayToolRoute[] = [
  {
    slug: 'array-length',
    category: 'inspect',
    label: 'Array Length Counter',
    shortLabel: 'Length',
    title: 'Array Length Counter Online',
    description:
      'Count array items online and inspect basic array stats from JSON arrays, comma-separated values, or line-separated values.',
  },
  {
    slug: 'remove-duplicates',
    category: 'transform',
    label: 'Remove Duplicates From Array',
    shortLabel: 'Unique',
    title: 'Remove Duplicates From Array Online',
    description:
      'Remove duplicate values from an array online while preserving the first occurrence. Useful for JavaScript arrays, IDs, and lists.',
  },
  {
    slug: 'sort-array',
    category: 'transform',
    label: 'Sort Array',
    shortLabel: 'Sort',
    title: 'Sort Array Online',
    description:
      'Sort array values online with developer-friendly JSON output. Sort strings, numbers, IDs, and mixed values quickly.',
  },
  {
    slug: 'reverse-array',
    category: 'transform',
    label: 'Reverse Array',
    shortLabel: 'Reverse',
    title: 'Reverse Array Online',
    description: 'Reverse array item order online and copy the result as formatted JSON.',
  },
  {
    slug: 'flatten-array',
    category: 'transform',
    label: 'Flatten Array',
    shortLabel: 'Flatten',
    title: 'Flatten Array Online',
    description:
      'Flatten nested JavaScript arrays online into a single-level array with formatted JSON output.',
  },
  {
    slug: 'chunk-array',
    category: 'transform',
    label: 'Chunk Array',
    shortLabel: 'Chunk',
    title: 'Chunk Array Online',
    description:
      'Split an array into chunks online by choosing a chunk size. Great for batching, pagination, and testing JavaScript utilities.',
  },
  {
    slug: 'array-to-csv',
    category: 'transform',
    label: 'Array to CSV',
    shortLabel: 'CSV',
    title: 'Array to CSV Converter Online',
    description:
      'Convert arrays to CSV online. Supports arrays of primitive values and arrays of objects with header columns.',
  },
  {
    slug: 'array-difference',
    category: 'compare',
    label: 'Array Difference',
    shortLabel: 'Difference',
    title: 'Array Difference Checker Online',
    description:
      'Compare two arrays online and find values present in the first array but missing from the second array.',
  },
  {
    slug: 'array-intersection',
    category: 'compare',
    label: 'Array Intersection',
    shortLabel: 'Intersection',
    title: 'Array Intersection Finder Online',
    description: 'Find common values between two arrays online using JSON-aware value comparison.',
  },
];

export function isArrayToolSlug(value: string): value is ArrayToolSlug {
  return ARRAY_TOOL_ROUTES.some((route) => route.slug === value);
}

export function getArrayToolRoute(slug: string | undefined): ArrayToolRoute | null {
  if (!slug || !isArrayToolSlug(slug)) {
    return null;
  }

  return ARRAY_TOOL_ROUTES.find((route) => route.slug === slug) ?? null;
}

export function getArrayToolPath(slug: ArrayToolSlug): string {
  return `/array-tools/${slug}`;
}

export function getArrayToolsByCategory(category: ArrayToolCategory): ArrayToolRoute[] {
  return ARRAY_TOOL_ROUTES.filter((route) => route.category === category);
}

export function getArrayToolStructuredData(route: ArrayToolRoute) {
  const path = getArrayToolPath(route.slug);
  const url = `${SITE_URL}${path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: route.title,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Any',
      url,
      description: route.description,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: SITE_NAME,
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: route.shortLabel,
          item: url,
        },
      ],
    },
  ];
}
